import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect, FieldDate, FieldTextarea,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/trainings';

const CATEGORIES = [
  { value: 'ONBOARDING', label: 'Oryantasyon' },
  { value: 'PRODUCT', label: 'Ürün' },
  { value: 'SALES', label: 'Satış' },
  { value: 'SAFETY', label: 'Güvenlik' },
  { value: 'COMPLIANCE', label: 'Uyum' },
  { value: 'SOFT_SKILLS', label: 'Yetkinlik' },
];

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const EMPTY_FORM = {
  textId: '',
  title: '',
  description: '',
  youtubeVideoId: '',
  durationSec: 0,
  category: 'PRODUCT',
  rewardPoints: 0,
  publishedAt: '',
  isActive: 'true',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

function toLocalDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
function fromLocalDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}

function rowToForm(r) {
  return {
    textId: pick(r, 'textId', 'TextId') ?? '',
    title: pick(r, 'title', 'Title') ?? '',
    description: pick(r, 'description', 'Description') ?? '',
    youtubeVideoId: pick(r, 'youtubeVideoId', 'YoutubeVideoId') ?? '',
    durationSec: pick(r, 'durationSec', 'DurationSec') ?? 0,
    category: pick(r, 'category', 'Category') ?? 'PRODUCT',
    rewardPoints: pick(r, 'rewardPoints', 'RewardPoints') ?? 0,
    publishedAt: toLocalDateInput(pick(r, 'publishedAt', 'PublishedAt')),
    isActive: (pick(r, 'isActive', 'IsActive') ?? false) ? 'true' : 'false',
  };
}

export function TrainingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Eğitim' : 'Eğitim Detayı', 'Saha temsilcisine atanan eğitim videosu kartı.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Tek kayıt yükleme: önce location.state.row; yoksa listeyi çekip id ile bul
  // (REST endpoint'inde GetById yok; liste ?take=500 üzerinden çözülür).
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    if (location.state?.row) {
      const sr = location.state.row;
      setForm(rowToForm(sr));
      // Nav-state satırı liste OData $select'inden gelir; audit alanlarını (createdDate/By…)
      // İÇERMEZ → info butonu gizli kalır. Tam kaydı arka planda listeden çekip audit'i ondan
      // doldur (REST'te GetById yok → liste ?take=500 üzerinden çözülür; paint'i bloklamaz).
      fetch(`${API_BASE}?take=500`, { headers: authHeaders() })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const items = pick(unwrap(data), 'items', 'Items') ?? [];
          const rec = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
          if (rec) setAudit({ createdBy: pick(rec, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(rec, 'createdDate', 'CreatedDate'), updatedBy: pick(rec, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(rec, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        })
        .catch(() => {});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=500`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Eğitim alınamadı (${res.status})`);
      const data = unwrap(await res.json());
      const items = pick(data, 'items', 'Items') ?? [];
      const found = (Array.isArray(items) ? items : []).find((r) => String(pick(r, 'id', 'Id')) === String(id));
      if (!found) throw new Error('Eğitim bulunamadı');
      setForm(rowToForm(found));
      setAudit({ createdBy: pick(found, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(found, 'createdDate', 'CreatedDate'), updatedBy: pick(found, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(found, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
    } catch (err) {
      showToast('error', err.message || 'Eğitim alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, location.state]);

  useEffect(() => { load(); }, [load]);

  // Klonla: /trainings/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  // location.key her navigasyonda değiştiği için bir klon yalnızca bir kez uygulanır.
  useEffect(() => {
    if (isNew && location.state?.clone) {
      setForm(location.state.clone);
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const buildBody = () => ({
    textId: form.textId.trim(),
    title: form.title.trim(),
    description: form.description.trim() || null,
    youtubeVideoId: form.youtubeVideoId.trim() || null,
    durationSec: Number(form.durationSec) || 0,
    category: form.category,
    rewardPoints: Number(form.rewardPoints) || 0,
    publishedAt: fromLocalDateInput(form.publishedAt),
    isActive: form.isActive === 'true',
  });

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan
  // (önceki eğitim backend'e kaydedilmiş olur). Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim()) { showToast('error', 'TextId zorunlu.'); return; }
    if (!form.title.trim()) { showToast('error', 'Başlık zorunlu.'); return; }
    if (!form.category) { showToast('error', 'Kategori zorunlu.'); return; }
    setSaving(true);
    try {
      const res = await fetch(isNew ? API_BASE : `${API_BASE}/${id}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(buildBody()),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Kayıt başarısız (${res.status})`);
      if (isNew) {
        if (andNew) {
          showToast('success', 'Eğitim eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Eğitim oluşturuldu.');
        setTimeout(() => navigate('/trainings'), 400);
        return;
      }
      showToast('success', 'Eğitim güncellendi.');
      setTimeout(() => navigate('/trainings'), 400);
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form bilgilerini taşıyarak yeni-kayıt ekranını aç.
  // Hiçbir şey kaydedilmez; kullanıcı "Kaydet" diyene kadar yeni eğitim oluşmaz.
  const handleClone = () => {
    navigate('/trainings/new', { state: { clone: { ...form } } });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Eğitim silindi.');
      setTimeout(() => navigate('/trainings'), 400);
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const title = isNew ? 'Yeni Eğitim' : (form.title || 'Eğitim Detayı');
  const statusBadge = !isNew && (form.isActive === 'true'
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="neutral">Pasif</StatusBadge>);

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/trainings')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni eğitim aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Eğitimler', to: '/trainings' }, { label: isNew ? 'Yeni Eğitim' : 'Eğitim Detayı', current: true }]}
      status={statusBadge}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'Training', entityId: id }}
      loading={loading}
    >
      <div className="dps-form">
        <Section icon="box" title="Eğitim Bilgileri" subtitle="Kod, başlık, kategori ve yayın durumu">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="textId" label="TextId" req mono placeholder="örn. tr-product-101" />
            <FieldSelect form={form} set={set} tried={tried} k="category" label="Kategori" req options={CATEGORIES} />
            <FieldText form={form} set={set} tried={tried} k="title" label="Başlık" req span placeholder="Başlık giriniz" />
            <FieldTextarea form={form} set={set} k="description" label="Açıklama" rows={3} span />
          </Grid>
        </Section>

        <Section icon="layers" title="Video & Ödül" subtitle="YouTube video, süre, ödül puanı ve yayın tarihi">
          <Grid cols={2}>
            <FieldText form={form} set={set} k="youtubeVideoId" label="YouTube Video ID" placeholder="dQw4w9WgXcQ" />
            <FieldText form={form} set={set} k="durationSec" label="Süre (saniye)" num type="number" />
            <FieldText form={form} set={set} k="rewardPoints" label="Ödül Puanı" num type="number" />
            <FieldDate form={form} set={set} k="publishedAt" label="Yayın Tarihi" />
            <FieldSelect form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS} />
          </Grid>
        </Section>
      </div>

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.title}”</strong> eğitimini silmek istediğinize emin misiniz?
          </p>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#dc2626' }}>
            Bu işlem geri alınamaz. Mobile'da bu eğitim bir sonraki sync'te kaybolacak.
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default TrainingDetailPage;
